import { AppStoreBadges } from '@/components/shared';
import { TeamImportDialog, TeamsList } from '@/components/teams';
import { useEventContext } from '@/pages/events/useEventContext';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  Input,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Smartphone, X } from 'lucide-react';
import { useState } from 'react';

export function EventTeamsTab() {
  const { event, isLoading } = useEventContext();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamEmoji, setTeamEmoji] = useState('');
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post(`/events/${event?.id}/teams`, {
        name: teamName.trim(),
        emoji: teamEmoji.trim() || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventTeams', event?.id] });
      toast.success('Team created successfully');
      setShowAddDialog(false);
      setTeamName('');
      setTeamEmoji('');
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to create team');
    },
  });

  const handleAddTeam = () => {
    if (!teamName.trim()) {
      toast.error('Team name is required');
      return;
    }
    addMutation.mutate();
  };

  // Export pulls the CSV via the existing apiClient (so auth + base
  // URL are handled the same way as every other request), then
  // triggers a browser download. `responseType: 'blob'` keeps axios
  // from trying to JSON-parse a `text/csv` body. Filename is derived
  // client-side from the event name to match the server's sanitised
  // pattern — Content-Disposition headers aren't accessible through
  // the apiClient wrapper.
  const handleExport = async () => {
    if (!event?.id) return;
    try {
      const blob = await apiClient.get<Blob>(`/events/${event.id}/teams/export`, {
        responseType: 'blob',
      });
      const safeName =
        (event.name || 'event').replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 60) || 'event';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}-teams.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to export teams');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Event not found</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto -m-4 sm:-m-6 p-4 sm:p-6 space-y-6">
      <TeamsList
        eventId={event.id}
        eventStatus={event.status}
        onAddTeam={() => setShowAddDialog(true)}
        onImportTeams={() => setShowImportDialog(true)}
        onExportTeams={handleExport}
      />

      {/* Team QR codes / join codes on this tab are redeemed inside the
          participant mobile app — surface the store links right where
          organizers share them. */}
      <div className="rounded-xl border bg-muted/30 p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            <div>
              <p className="text-sm font-medium">
                Participants need the Destination Hunts app
              </p>
              <p className="text-sm text-muted-foreground">
                Team QR codes and join codes are scanned or entered in the mobile app.
              </p>
            </div>
          </div>
          <AppStoreBadges size="compact" className="shrink-0" />
        </div>
      </div>

      <TeamImportDialog
        eventId={event.id}
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
      />

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Add New Team</DialogTitle>
                <DialogDescription>Create a new team for this event</DialogDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAddDialog(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <FormFieldGroup name="teamName" label="Team Name" required>
              <Input
                id="teamName"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Enter team name"
              />
            </FormFieldGroup>

            <FormFieldGroup name="teamEmoji" label="Team Emoji" description="Optional emoji for the team">
              <Input
                id="teamEmoji"
                value={teamEmoji}
                onChange={(e) => setTeamEmoji(e.target.value)}
                placeholder="e.g., 🦊"
                maxLength={10}
              />
            </FormFieldGroup>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTeam} loading={addMutation.isPending}>
              Create Team
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
